/**
 * Turns accumulated look input into camera orientation.
 *
 * Yaw and pitch are held in a ref and written straight onto the camera inside `useFrame`.
 * They are never React state: this changes every frame, and per-frame data through React
 * would re-render the tree sixty times a second (CLAUDE.md, "never setState in the render
 * loop").
 *
 * Rotation is applied as an explicit YXZ Euler rather than by accumulating quaternions,
 * because that is what keeps roll at exactly zero. Accumulating rotations lets floating
 * point error tilt the horizon over a long session.
 */

import { useFrame, useThree } from '@react-three/fiber';
import { useRef } from 'react';
import { consumeLook, inputState } from '../input/inputState';
import { applyLookSensitivity } from '../input/sensitivity';
import { useGameStore } from '../store/gameStore';
import { MAX_PITCH } from './constants';

export type LookAngles = { yaw: number; pitch: number };

export function usePlayerLook(initialYaw = 0): React.RefObject<LookAngles> {
  const camera = useThree((state) => state.camera);
  const angles = useRef<LookAngles>({ yaw: initialYaw, pitch: 0 });

  useFrame(() => {
    // Read settings imperatively rather than subscribing: subscribing would re-render this
    // component when the player moves the sensitivity slider, and the value is only needed
    // here, inside the loop.
    const { settings } = useGameStore.getState();
    const delta = applyLookSensitivity(consumeLook(inputState), settings);

    if (delta.x !== 0 || delta.y !== 0) {
      const next = angles.current;
      // Dragging right turns right, so yaw decreases: +x screen is a clockwise turn when
      // looking down the -Z axis.
      next.yaw -= delta.x;
      next.pitch = Math.max(-MAX_PITCH, Math.min(MAX_PITCH, next.pitch - delta.y));
    }

    camera.rotation.order = 'YXZ';
    camera.rotation.y = angles.current.yaw;
    camera.rotation.x = angles.current.pitch;
    camera.rotation.z = 0;
  });

  return angles;
}
